import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { Upload, Image as ImageIcon, Link2, X } from "lucide-react";

interface ImageUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (url: string) => void;
}

export const ImageUploadModal = ({ isOpen, onClose, onInsert }: ImageUploadModalProps) => {
  const [method, setMethod] = useState<'url' | 'upload'>('upload');
  const [url, setUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result as string);
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleInsert = () => {
    if (method === 'url' && url.trim()) {
      onInsert(url.trim());
      resetAndClose();
    } else if (method === 'upload' && preview) {
      onInsert(preview);
      resetAndClose();
    }
  };

  const resetAndClose = () => {
    setUrl("");
    setFile(null);
    setPreview(null);
    setMethod('upload');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) resetAndClose();
    }}>
      <DialogContent className="border-[3px] border-dark max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Inserisci Immagine
          </DialogTitle>
        </DialogHeader>

        {/* Tab Selection */}
        <div className="flex gap-2">
          <BrutalButton
            type="button"
            size="sm"
            variant={method === 'upload' ? 'primary' : 'outline'}
            onClick={() => setMethod('upload')}
            className="flex-1"
          >
            <Upload className="w-4 h-4 mr-2" />
            Carica File
          </BrutalButton>
          <BrutalButton
            type="button"
            size="sm"
            variant={method === 'url' ? 'primary' : 'outline'}
            onClick={() => setMethod('url')}
            className="flex-1"
          >
            <Link2 className="w-4 h-4 mr-2" />
            Da URL
          </BrutalButton>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {method === 'upload' ? (
            <>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                id="image-upload-editor"
              />
              <label
                htmlFor="image-upload-editor"
                className="flex flex-col items-center gap-2 p-6 border-[3px] border-dashed border-dark cursor-pointer hover:bg-accent/10 transition-colors"
              >
                {preview ? (
                  <div className="relative w-full">
                    <img
                      src={preview}
                      alt="Preview"
                      className="max-h-48 mx-auto border-2 border-dark"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        setFile(null);
                        setPreview(null);
                      }}
                      className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground p-1 rounded-full border-2 border-dark"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-muted-foreground" />
                    <p className="text-sm font-medium">Carica immagine</p>
                    <p className="text-xs text-muted-foreground">JPG, PNG, GIF, WEBP</p>
                  </>
                )}
              </label>
            </>
          ) : (
            <div>
              <label className="block font-heading text-sm mb-2">
                URL Immagine
              </label>
              <BrutalInput
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://esempio.com/immagine.jpg"
                type="url"
              />
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <BrutalButton
            type="button"
            variant="outline"
            onClick={resetAndClose}
          >
            Annulla
          </BrutalButton>
          <BrutalButton
            type="button"
            onClick={handleInsert}
            disabled={method === 'url' ? !url.trim() : !preview}
          >
            <ImageIcon className="w-4 h-4 mr-2" />
            Inserisci Immagine
          </BrutalButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
